// src/modules/requerimiento/views/devolucion/Programacion.jsx
// Trabajo de campo de una devolución (ya no hay etapa de Programación): por cada vehículo de la
// coordinación se devuelven sus equipos (DevolucionPlacaModal) y, al terminar, "Finalizar trabajo
// de campo" pasa a Revisión en Sistemas. Diseño en tarjetas, como el campo de adición.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Alert, Button, Card, CardContent, LinearProgress, Stack,
} from "@mui/material";
import { useParams } from "react-router-dom";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";

import { getRequerimiento, updateRequerimiento } from "../../services/reqSolicitudes";
import { getDevolucionAsignados, postDevolverReq } from "../../services/devolucion";
import { getProfile } from "../../services/auth";
import DevolucionPlacaModal from "../../components/devolucion/DevolucionPlacaModal";
import FinalizarTrabajoCampoButton from "../../components/FinalizarTrabajoCampoButton";
import ENV from "../../../../config/env";
import CentroEquipoDetalleDialog from "../../components/CentroEquipoDetalleDialog";
import { Observacion } from "../../components/adicion/EquipoInstaladoDialog";
import { FilaModemDevuelto, ChipDevueltoCard, ModemDevueltoDialog } from "../../components/devolucion/DevueltosComponents";

const VERDE = "#16A34A";
const AMBAR = "#F59E0B";

const normPlaca = (p) => String(p ?? "").trim().toUpperCase();

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("es-BO");
}

export default function DevolucionProgramacion(props) {
  const { onRefreshRequerimiento, onToast } = props;
  const params = useParams();
  const cod    = useMemo(() => props?.cod ?? params?.cod, [props?.cod, params?.cod]);

  const [saving,          setSaving]          = useState(false);
  const [req,             setReq]             = useState(null);
  const [dataDev,         setDataDev]         = useState({ modems: [], chips: [], placas: { ingresadas: [], existentes: [], faltantes: [] } });
  const [loadingDev,      setLoadingDev]      = useState(false);
  const [errDev,          setErrDev]          = useState(null);
  const [defaultSucursal, setDefaultSucursal] = useState("");
  const [modalOpen,       setModalOpen]       = useState(false);
  const [modalPlaca,      setModalPlaca]      = useState(null);
  const [modemSel,        setModemSel]        = useState(null);
  const [centroEquipo,    setCentroEquipo]    = useState(null);

  const avisar = useCallback((sev, msg) => onToast?.({ sev, msg }), [onToast]);

  const toAbsUrl = useCallback((u) => {
    if (!u) return "";
    if (/^https?:\/\//i.test(u)) return u;
    return ENV.API_BASE_URL ? `${ENV.API_BASE_URL}${u}` : u;
  }, []);

  const fetchReq = useCallback(async () => {
    if (!cod) return;
    try {
      const { data } = await getRequerimiento(cod);
      setReq(data);
    } catch { /* silencioso — datos de cabecera opcionales */ }
  }, [cod]);

  const fetchDevolucion = useCallback(async () => {
    if (!cod) return;
    setLoadingDev(true);
    setErrDev(null);
    try {
      const { data } = await getDevolucionAsignados(cod);
      setDataDev({
        modems: data?.["Modems devueltos"]                 ?? [],
        chips:  data?.["Chips devueltos"]                  ?? [],
        placas: data?.["Placas asignadas para la devolucion"] ?? { ingresadas: [], existentes: [], faltantes: [] },
      });
    } catch (e) {
      setErrDev(e?.response?.data?.message || e?.message || "Error al cargar datos de devolucion");
    } finally {
      setLoadingDev(false);
    }
  }, [cod]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchReq(), fetchDevolucion()]);
  }, [fetchReq, fetchDevolucion]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  useEffect(() => {
    (async () => {
      try {
        const user = await getProfile();
        const raw  = user?.sucursal ?? "";
        setDefaultSucursal(raw === "La Paz" ? "La Paz(El Alto)" : raw);
      } catch { setDefaultSucursal(""); }
    })();
  }, []);

  const handleOpenModal  = useCallback((placa) => { setModalPlaca(placa); setModalOpen(true); }, []);
  const handleCloseModal = useCallback(() => { setModalOpen(false); setModalPlaca(null); }, []);

  const handleSaveModal = useCallback(async ({ tipoDevolucion, selectedModems, sucursal, observaciones, imagenes }) => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("tipo_devolucion", Number(tipoDevolucion) || 1);
      formData.append("sucursal", sucursal || "");
      selectedModems.forEach((id) => formData.append("num_sim[]", id));
      formData.append("observacion", JSON.stringify(Object.fromEntries(selectedModems.map((id) => [id, observaciones?.[id] ?? ""]))));
      selectedModems.forEach((id) => { if (imagenes?.[id]) formData.append(`imagen_${id}`, imagenes[id]); });

      await postDevolverReq(cod, formData, { headers: { "Content-Type": "multipart/form-data" } });
      // Devolución emitida antes del cambio (aún en Coordinación/Programación): con el primer
      // equipo devuelto pasa a Trabajo de campo. Si falla, la devolución ya quedó registrada.
      if ([1, 2].includes(Number(req?.estado)) && !Number(req?.es_transferencia_cliente)) {
        await updateRequerimiento(cod, { estado: 3 }).catch(() => {});
      }
      avisar("success", "Devolución registrada");
      await fetchDevolucion();
      await onRefreshRequerimiento?.();
      setModalOpen(false);
      setModalPlaca(null);
    } catch (e) {
      avisar("error", e?.response?.data?.error || e?.response?.data?.message || e?.message || "No se pudo registrar la devolución");
    } finally {
      setSaving(false);
    }
  }, [cod, req?.estado, req?.es_transferencia_cliente, fetchDevolucion, onRefreshRequerimiento, avisar]);

  const handleDoneFinalizar = useCallback(async () => {
    await refreshAll();
    onRefreshRequerimiento?.();
  }, [refreshAll, onRefreshRequerimiento]);

  // Cada vehículo con los equipos que ya se le devolvieron; los devueltos de una placa que no
  // está en la coordinación van aparte.
  const { vehiculos, otrosDevueltos } = useMemo(() => {
    const ingresadas = dataDev.placas?.ingresadas ?? [];
    const existentes = new Set((dataDev.placas?.existentes ?? []).map(normPlaca));
    const faltantes  = new Set((dataDev.placas?.faltantes ?? []).map(normPlaca));
    const detalleDe  = new Map((dataDev.placas?.detalle ?? []).map((d) => [normPlaca(d.placa), d]));
    const claves = new Set(ingresadas.map(normPlaca));
    return {
      vehiculos: ingresadas.map((p) => ({
        placa: p,
        verificacion: existentes.has(normPlaca(p)) ? "encontrada" : faltantes.has(normPlaca(p)) ? "no_encontrada" : "sin_verificar",
        detalle: detalleDe.get(normPlaca(p)) || null,
        devueltos: dataDev.modems.filter((m) => normPlaca(m.placa) === normPlaca(p)),
      })),
      otrosDevueltos: dataDev.modems.filter((m) => !claves.has(normPlaca(m.placa))),
    };
  }, [dataDev]);

  const conDevolucion = vehiculos.filter((v) => v.devueltos.length > 0).length;
  const observacion = String(req?.obs2 ?? req?.observacion ?? "").trim();

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, pb: 10 }}>
      {/* Avance */}
      {vehiculos.length > 0 && !loadingDev && (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: "10px" }}>
          <CardContent sx={{ py: 1.75, "&:last-child": { pb: 1.75 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {conDevolucion} de {vehiculos.length} {vehiculos.length === 1 ? "vehículo" : "vehículos"} con devolución
              </Typography>
              {conDevolucion >= vehiculos.length && <CheckCircleIcon color="success" />}
            </Stack>
            <LinearProgress
              variant="determinate"
              color={conDevolucion >= vehiculos.length ? "success" : "warning"}
              value={(conDevolucion / vehiculos.length) * 100}
              sx={{ height: 8, borderRadius: 99, my: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              {conDevolucion >= vehiculos.length
                ? "Listo: ya puedes finalizar el trabajo de campo."
                : "Usa \"Devolver equipos\" en cada vehículo para registrar qué se retiró, con su foto."}
            </Typography>
          </CardContent>
        </Card>
      )}

      {observacion && <Box sx={{ mb: 2 }}><Observacion texto={observacion} /></Box>}

      {loadingDev ? (
        <LinearProgress />
      ) : errDev ? (
        <Alert severity="error" action={<Button size="small" onClick={fetchDevolucion}>Reintentar</Button>}>{errDev}</Alert>
      ) : (
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Vehículos</Typography>
            {vehiculos.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                La coordinación no tiene placas. Agrégalas con "Editar coordinación".
              </Typography>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5, alignItems: "start" }}>
                {vehiculos.map((v) => (
                  <VehiculoCard
                    key={v.placa}
                    vehiculo={v}
                    onDevolver={() => handleOpenModal(v.placa)}
                    onVerModem={setModemSel}
                  />
                ))}
              </Box>
            )}
          </Box>

          {otrosDevueltos.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Otros equipos devueltos</Typography>
              <Card variant="outlined" sx={{ borderRadius: "10px" }}>
                <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
                  {otrosDevueltos.map((m) => (
                    <FilaModemDevuelto key={m.num_sim || m.id} modem={m} conPlaca onClick={() => setModemSel(m)} />
                  ))}
                </Stack>
              </Card>
            </Box>
          )}

          {dataDev.chips.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Chips devueltos</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
                {dataDev.chips.map((c) => <ChipDevueltoCard key={c.numero} chip={c} />)}
              </Box>
            </Box>
          )}
        </Stack>
      )}

      <DevolucionPlacaModal
        open={modalOpen} placa={modalPlaca} cliente={req?.razon_social}
        serv={req?.cod_servicio_cliente} onClose={handleCloseModal}
        onSave={handleSaveModal} defaultSucursal={defaultSucursal} saving={saving}
      />

      <FinalizarTrabajoCampoButton cod={cod} onDone={handleDoneFinalizar} sticky sx={{ mt: 2 }} />

      <ModemDevueltoDialog
        modem={modemSel}
        cod={cod}
        toAbsUrl={toAbsUrl}
        onClose={() => setModemSel(null)}
        onChanged={fetchDevolucion}
        onToast={onToast}
        onVerEquipo={(id) => setCentroEquipo({ tipo: "modem", identificador: String(id) })}
      />

      <CentroEquipoDetalleDialog
        open={!!centroEquipo}
        tipo={centroEquipo?.tipo || "modem"}
        identificador={centroEquipo?.identificador}
        onClose={() => setCentroEquipo(null)}
      />
    </Box>
  );
}

// ── Tarjeta de un vehículo ─────────────────────────────────────

const VERIFICACION = {
  encontrada:    { texto: "Encontrada",    color: VERDE,        Icono: CheckCircleIcon },
  no_encontrada: { texto: "No encontrada", color: "error.main", Icono: CancelIcon },
  sin_verificar: { texto: "Sin verificar", color: AMBAR,        Icono: HelpOutlineIcon },
};

/**
 * Un vehículo de la devolución: placa, cliente y si se encontró; abajo los equipos que ya se
 * le devolvieron y el botón para devolver (más) equipos.
 */
function VehiculoCard({ vehiculo, onDevolver, onVerModem }) {
  const { placa, detalle, devueltos } = vehiculo;
  const ver = VERIFICACION[vehiculo.verificacion];
  const hecho = devueltos.length > 0;
  const relaciones = Array.isArray(detalle?.relaciones) ? detalle.relaciones : [];
  const estados = [
    detalle?.estado_vehiculo && `Vehículo ${detalle.estado_vehiculo}`,
    detalle?.estado_relacion && `Relación ${detalle.estado_relacion}`,
  ].filter(Boolean).join(" · ");
  const fechas = [
    formatDate(detalle?.fecha_asignacion) && `Asignado ${formatDate(detalle.fecha_asignacion)}`,
    formatDate(detalle?.fecha_fin) && `Fin ${formatDate(detalle.fecha_fin)}`,
  ].filter(Boolean).join(" · ");

  return (
    <Card variant="outlined" sx={{ borderRadius: "10px", borderColor: hecho ? "success.light" : "divider" }}>
      <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 44, height: 44, borderRadius: "8px", bgcolor: "action.hover", color: "text.secondary", display: "grid", placeItems: "center", flexShrink: 0 }}>
            <DirectionsCarIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>{placa}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {[detalle?.razon_social, detalle?.descripcion].filter(Boolean).join(" · ") || "Sin cliente"}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            <ver.Icono sx={{ color: ver.color, fontSize: 18 }} />
            <Typography variant="caption" sx={{ color: ver.color, fontWeight: 700 }}>{ver.texto}</Typography>
          </Stack>
        </Stack>

        {(estados || fechas) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {[estados, fechas].filter(Boolean).join(" · ")}
          </Typography>
        )}

        {relaciones.length > 1 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1.8 }}>
              Relaciones ({relaciones.length})
            </Typography>
            {relaciones.map((rel, idx) => (
              <Stack key={`${rel.cod_servicio_cliente}-${idx}`} direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {rel.razon_social || rel.cod_servicio_cliente}
                </Typography>
                <Typography variant="caption" color="text.secondary">{rel.estado_relacion || "—"}</Typography>
              </Stack>
            ))}
          </Box>
        )}

        {hecho && (
          <Box sx={{ mt: 1.5, borderRadius: "8px", border: 1, borderColor: "divider", overflow: "hidden" }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 1.25, py: 0.75, bgcolor: "action.hover" }}>
              <CheckCircleIcon sx={{ color: VERDE, fontSize: 18 }} />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {devueltos.length === 1 ? "1 equipo devuelto" : `${devueltos.length} equipos devueltos`}
              </Typography>
            </Stack>
            <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
              {devueltos.map((m) => (
                <FilaModemDevuelto key={m.num_sim || m.id} modem={m} onClick={() => onVerModem(m)} />
              ))}
            </Stack>
          </Box>
        )}

        <Button
          fullWidth
          variant={hecho ? "outlined" : "contained"}
          startIcon={<AssignmentReturnIcon />}
          onClick={onDevolver}
          sx={{ mt: 1.5, textTransform: "none", fontWeight: 700 }}
        >
          {hecho ? "Devolver otro equipo" : "Devolver equipos"}
        </Button>
      </CardContent>
    </Card>
  );
}
